import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';

/**
 * `<select>` de filtre labellisé, RÉUTILISABLE par les deux barres de filtres
 * (journal des erreurs de fichiers ET journal des requêtes). Générique sur le type
 * de valeur : `''` (chaîne vide) = « Tous » (aucun filtre). Chaque option porte un
 * libellé i18n. Le label est lié via `htmlFor` (accessibilité), le contrôle ≥44px.
 */
export interface FilterOption<V extends string> {
  value: V | '';
  labelKey: MessageKey;
}

export interface FilterSelectProps<V extends string> {
  id: string;
  labelKey: MessageKey;
  value: V | '';
  options: readonly FilterOption<V>[];
  onChange: (value: V | '') => void;
}

export function FilterSelect<V extends string>({
  id,
  labelKey,
  value,
  options,
  onChange,
}: FilterSelectProps<V>): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {t(labelKey)}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as V | '')}
        className="h-11 min-w-[10rem] rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {t(opt.labelKey)}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * `<select>` de filtre à options DYNAMIQUES en texte libre (ex. liste des
 * utilisateurs, dont les libellés sont des données et non des clés i18n). L'option
 * « Tous » reste une clé i18n. Distinct de `FilterSelect` pour ne pas mélanger
 * clés i18n et données utilisateur.
 */
export interface DynamicOption {
  value: string;
  label: string;
}

export interface DynamicFilterSelectProps {
  id: string;
  labelKey: MessageKey;
  allLabelKey: MessageKey;
  value: string;
  options: readonly DynamicOption[];
  onChange: (value: string) => void;
}

export function DynamicFilterSelect({
  id,
  labelKey,
  allLabelKey,
  value,
  options,
  onChange,
}: DynamicFilterSelectProps): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {t(labelKey)}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 min-w-[10rem] max-w-[16rem] rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <option value="">{t(allLabelKey)}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
